import { useMemo, useState } from 'react';
import {
  CheckIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { loadStickyNotes, saveStickyNotes } from '@/components/StickyNotes/stickyNotesStorage';
import { STICKY_NOTE_COLORS, type StickyNote, type StickyNoteColor } from '@/components/StickyNotes/types';
import './ProfileStickyNotes.css';

const COLOR_LABELS: Record<StickyNoteColor, string> = {
  yellow: 'Yellow',
  green: 'Green',
  orange: 'Orange',
  red: 'Red',
  blue: 'Blue',
  pink: 'Pink',
};

function noteTitle(text: string): string {
  const firstLine = text
    .split('\n')
    .map(line => line.trim())
    .find(Boolean);
  if (!firstLine) return 'Untitled note';
  return firstLine.length > 48 ? `${firstLine.slice(0, 48)}…` : firstLine;
}

function noteBody(text: string): string | null {
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  if (lines.length <= 1) return null;
  const rest = lines.slice(1).join(' ');
  return rest.length > 140 ? `${rest.slice(0, 140)}…` : rest;
}

function formatNoteDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Today, ${time}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

type ProfileStickyNotesProps = {
  stickyNotesClass: string;
};

export default function ProfileStickyNotes({ stickyNotesClass }: ProfileStickyNotesProps) {
  const context = userAppContext();
  const user = context?.user ?? null;

  const [notes, setNotes] = useState<StickyNote[]>(() => loadStickyNotes(user));
  const [colorFilter, setColorFilter] = useState<'all' | StickyNoteColor>('all');
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editColor, setEditColor] = useState<StickyNoteColor>('yellow');

  const persist = (next: StickyNote[]) => {
    setNotes(next);
    saveStickyNotes(user, next);
  };

  const filteredNotes = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notes
      .filter(note => (colorFilter === 'all' ? true : note.color === colorFilter))
      .filter(note => (q ? note.text.toLowerCase().includes(q) : true))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [notes, colorFilter, query]);

  const handleDelete = (id: string) => {
    if (editingId === id) {
      setEditingId(null);
    }
    persist(notes.filter(note => note.id !== id));
  };

  const startEdit = (note: StickyNote) => {
    setEditingId(note.id);
    setEditText(note.text);
    setEditColor(note.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = () => {
    if (!editingId) return;
    persist(
      notes.map(note =>
        note.id === editingId
          ? {
              ...note,
              text: editText,
              color: editColor,
              updatedAt: new Date().toISOString(),
            }
          : note,
      ),
    );
    setEditingId(null);
    setEditText('');
  };

  return (
    <div className={`${stickyNotesClass} profile-sticky-notes`} id="stickyNotes">
      <div className="profile-sticky-notes__toolbar">
        <h2 className="profile-sticky-notes__title">Sticky Notes</h2>
        <div className="profile-sticky-notes__toolbar-actions">
          <label className="profile-sticky-notes__search">
            <MagnifyingGlassIcon className="profile-sticky-notes__icon" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search notes…"
              aria-label="Search sticky notes"
            />
          </label>
        </div>
      </div>

      <p className="profile-sticky-notes__privacy">
        Notes are saved only on this device (browser local storage). They are not stored in DISGENET
        databases.
      </p>

      <div className="profile-sticky-notes__filters" role="tablist" aria-label="Filter by color">
        <button
          type="button"
          role="tab"
          aria-selected={colorFilter === 'all'}
          className={`profile-sticky-notes__chip ${colorFilter === 'all' ? 'is-active' : ''}`}
          onClick={() => setColorFilter('all')}
        >
          All
        </button>
        {STICKY_NOTE_COLORS.map(color => (
          <button
            key={color}
            type="button"
            role="tab"
            aria-selected={colorFilter === color}
            className={`profile-sticky-notes__chip profile-sticky-notes__chip--${color} ${
              colorFilter === color ? 'is-active' : ''
            }`}
            onClick={() => setColorFilter(color)}
          >
            {COLOR_LABELS[color]}
          </button>
        ))}
      </div>

      {filteredNotes.length === 0 ? (
        <p className="profile-sticky-notes__empty">
          {notes.length === 0
            ? 'No sticky notes yet. Create one from tools or search results.'
            : 'No notes match your filters.'}
        </p>
      ) : (
        <div className="profile-sticky-notes__grid">
          {filteredNotes.map(note => {
            const isEditing = editingId === note.id;
            const cardColor = isEditing ? editColor : note.color;
            const body = noteBody(note.text);

            return (
              <article
                key={note.id}
                className={`profile-sticky-notes__card profile-sticky-notes__card--${cardColor}${
                  isEditing ? ' is-editing' : ''
                }`}
              >
                <div className="profile-sticky-notes__card-top">
                  {isEditing ? (
                    <div className="profile-sticky-notes__edit-colors" role="group" aria-label="Note color">
                      {STICKY_NOTE_COLORS.map(color => (
                        <button
                          key={color}
                          type="button"
                          className={`profile-sticky-notes__edit-swatch profile-sticky-notes__card--${color}${
                            editColor === color ? ' is-selected' : ''
                          }`}
                          onClick={() => setEditColor(color)}
                          aria-label={`Set color ${COLOR_LABELS[color]}`}
                          aria-pressed={editColor === color}
                        />
                      ))}
                    </div>
                  ) : (
                    <span className="profile-sticky-notes__tag">{COLOR_LABELS[note.color]}</span>
                  )}
                  <span className={`profile-sticky-notes__status ${note.minimized ? 'is-saved' : 'is-open'}`}>
                    {note.minimized ? 'Saved' : 'Open'}
                  </span>
                </div>

                {isEditing ? (
                  <textarea
                    className="profile-sticky-notes__edit-textarea"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    aria-label={`Edit note: ${noteTitle(note.text)}`}
                    placeholder="Write a note…"
                    autoFocus
                  />
                ) : (
                  <>
                    <h3 className="profile-sticky-notes__card-title">{noteTitle(note.text)}</h3>
                    {body && <p className="profile-sticky-notes__card-body">{body}</p>}
                  </>
                )}

                <div className="profile-sticky-notes__card-footer">
                  <time dateTime={note.updatedAt}>{formatNoteDate(note.updatedAt)}</time>
                  <div className="profile-sticky-notes__card-actions">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="profile-sticky-notes__card-btn"
                          onClick={saveEdit}
                          aria-label={`Save note: ${noteTitle(note.text)}`}
                          title="Save"
                        >
                          <CheckIcon className="profile-sticky-notes__icon" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="profile-sticky-notes__card-btn"
                          onClick={cancelEdit}
                          aria-label="Cancel edit"
                          title="Cancel"
                        >
                          <XMarkIcon className="profile-sticky-notes__icon" aria-hidden />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="profile-sticky-notes__card-btn"
                          onClick={() => startEdit(note)}
                          aria-label={`Edit note: ${noteTitle(note.text)}`}
                          title="Edit"
                        >
                          <PencilSquareIcon className="profile-sticky-notes__icon" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="profile-sticky-notes__card-btn profile-sticky-notes__card-btn--danger"
                          onClick={() => handleDelete(note.id)}
                          aria-label={`Delete note: ${noteTitle(note.text)}`}
                          title="Delete"
                        >
                          <TrashIcon className="profile-sticky-notes__icon" aria-hidden />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
